import dayjs from 'dayjs';
import type { ICampaignCategory } from '../../../components/EsForm/CampaignCategorySelector';
import { emptyWizardCategory } from '../contestManage/contestCategoryWizardUtils';
import { reconcilePrizeStructureWithTiers, tierLabelsFromCategory } from './awardPresets';
import { generateTimelinePhasesFromAnswers } from './generateTimelineFromAnswers';
import { parseCsvSelection } from './csvSelection';
import { criteriaLabelsFromSelection, defaultCriteriaSelection } from './judgingCriteriaPresets';
import { buildFundingAwardRowsFromCategories, formatFundingAwardTitle, formatUsdFromCents } from './fundingUtils';
import { CONTEST_WIZARD_INITIAL, type ContestWizardState, type SubmissionState } from './types';
import { plainTextFromHtml } from './richTextUtils';

const pick = <T>(arr: readonly T[]): T => arr[Math.floor(Math.random() * arr.length)];

export const SUBMISSION_TYPE_OPTIONS = [
    {
        value: 'public_submission' as const,
        label: 'Public',
        message: 'Submissions can be public to be eligible. This enables voting options.',
    },
    {
        value: 'private_submission' as const,
        label: 'Private',
        message:
            'Submissions MUST be private (unpublished) to be eligible. This disables public voting and only the host, team members and jurors can see the entries.',
    },
];

const CONTEST_TITLES = [
    'Midnight Summer Film Challenge',
    'Urban Stories Short Film Contest',
    'Brand Vision Creative Showcase',
    'Emerging Voices Film Prize',
    'Noir Nights Filmmaker Open Call',
];

const CONTEST_DESCRIPTIONS = [
    'We are looking for bold, original short films that capture authentic human stories. Submissions should feel cinematic, intentional, and ready for festival consideration.',
    'Launch your creative campaign with a clear brief: showcase craft, originality, and emotional impact. Winning entries may be featured across our brand channels.',
    'This contest invites filmmakers to interpret our brand through compelling visual storytelling. Think atmosphere, character, and a memorable final frame.',
];

const REQUIREMENT_SAMPLES = [
    'Original work created for this campaign',
    'Minimum 60 seconds, maximum 3 minutes',
    'Include on-screen title card with creator name',
    'No copyrighted music without proper clearance',
    'Submit in 16:9 MP4 format',
];

const CATEGORY_NAMES = ['Main Competition', 'Open Category', 'Featured Shorts', 'Rising Filmmaker'];
const CATEGORY_DESCRIPTIONS = [
    'Primary category for all eligible short film submissions.',
    'Open to creators worldwide meeting the technical specifications.',
    'Best fit for narrative-driven short films under three minutes.',
];

export interface ContestQuickCreateDraft {
    submission_state: SubmissionState;
    submission_state_message: string;
    title: string;
    description: string;
    requirements: string[];
    categories_enabled: boolean;
    categories: ICampaignCategory[];
}

function sampleQuickCreateCategory(): ICampaignCategory {
    return {
        ...emptyWizardCategory(),
        category_name: pick(CATEGORY_NAMES),
        category_description: pick(CATEGORY_DESCRIPTIONS),
        with_fee: true,
        tier_fee: [
            { label: 'Gold', fee: 99, currency: 'USD' },
            { label: 'Silver', fee: 49, currency: 'USD' },
        ],
        prize_structure: [
            {
                position: '1st Place',
                payouts: [
                    { label: 'Gold', prize: 600, additional_prize: ['Certificate'] },
                    { label: 'Silver', prize: 100, additional_prize: [] },
                ],
            },
        ],
    };
}

export function isQuickCreateCategoryDetailsFilled(draft: ContestQuickCreateDraft): boolean {
    if (!draft.categories_enabled) return false;
    const cat = draft.categories[0];
    return Boolean(cat?.category_name?.trim() && plainTextFromHtml(cat.category_description || ''));
}

/** True once category setup has begun — keeps the form visible if name/description are cleared later. */
export function isQuickCreateCategorySetupStarted(draft: ContestQuickCreateDraft): boolean {
    if (!draft.categories_enabled) return false;
    const cat = draft.categories[0];
    if (!cat) return false;
    if (cat.category_name?.trim() || plainTextFromHtml(cat.category_description || '')) return true;
    if ((cat.tier_fee || []).some(t => t.label?.trim())) return true;
    if ((cat.prize_structure || []).length > 0) return true;
    return false;
}

export function isQuickCreateCategoryAwardsConfigured(draft: ContestQuickCreateDraft): boolean {
    if (!draft.categories_enabled) return true;
    if (!isQuickCreateCategoryDetailsFilled(draft)) return false;
    const cat = draft.categories[0];
    return (cat.prize_structure || []).some(p => (p.payouts || []).some(po => Number(po.prize) > 0));
}

export interface QuickCreateEditableAwardRow {
    placementIndex: number;
    payoutIndex: number;
    title: string;
    prize: number;
}

export interface QuickCreateEditableTierRow {
    tierIndex: number;
    label: string;
    fee: number;
}

function cloneQuickCreateCategories(categories: ICampaignCategory[]): ICampaignCategory[] {
    return categories.map(c => ({
        ...c,
        tier_fee: (c.tier_fee || []).map(t => ({ ...t })),
        prize_structure: (c.prize_structure || []).map(p => ({
            ...p,
            payouts: (p.payouts || []).map(po => ({
                ...po,
                additional_prize: [...(po.additional_prize || [])],
            })),
        })),
    }));
}

export function getQuickCreateEditableTierRows(category: ICampaignCategory): QuickCreateEditableTierRow[] {
    return (category.tier_fee || []).map((tier, tierIndex) => ({
        tierIndex,
        label: tier.label || '',
        fee: Number(tier.fee) || 0,
    }));
}

export function updateQuickCreateTierLabel(
    categories: ICampaignCategory[],
    tierIndex: number,
    label: string,
): ICampaignCategory[] {
    const cats = cloneQuickCreateCategories(categories.length ? categories : [emptyWizardCategory()]);
    const tiers = [...(cats[0]?.tier_fee || [])];
    const tier = tiers[tierIndex];
    if (!tier) return categories;
    tiers[tierIndex] = { ...tier, label, currency: tier.currency || 'USD' };
    cats[0] = reconcilePrizeStructureWithTiers({
        ...cats[0],
        tier_fee: tiers,
        with_fee: tiers.some(t => Number(t.fee) > 0),
    });
    return cats;
}

export function updateQuickCreateTierFee(
    categories: ICampaignCategory[],
    tierIndex: number,
    fee: number,
): ICampaignCategory[] {
    const cats = cloneQuickCreateCategories(categories.length ? categories : [emptyWizardCategory()]);
    const tiers = [...(cats[0]?.tier_fee || [])];
    const tier = tiers[tierIndex];
    if (!tier) return categories;
    tiers[tierIndex] = { ...tier, fee, currency: tier.currency || 'USD' };
    cats[0] = {
        ...cats[0],
        tier_fee: tiers,
        with_fee: tiers.some(t => Number(t.fee) > 0),
    };
    return cats;
}

export function removeQuickCreateTier(categories: ICampaignCategory[], tierIndex: number): ICampaignCategory[] {
    const cats = cloneQuickCreateCategories(categories.length ? categories : [emptyWizardCategory()]);
    const tiers = [...(cats[0]?.tier_fee || [])];
    if (!tiers[tierIndex]) return categories;
    tiers.splice(tierIndex, 1);
    cats[0] = reconcilePrizeStructureWithTiers({
        ...cats[0],
        tier_fee: tiers,
        with_fee: tiers.some(t => Number(t.fee) > 0),
    });
    return cats;
}

export function getQuickCreateEditableAwardRows(category: ICampaignCategory): QuickCreateEditableAwardRow[] {
    const rows: QuickCreateEditableAwardRow[] = [];
    for (let placementIndex = 0; placementIndex < (category.prize_structure?.length ?? 0); placementIndex++) {
        const placement = category.prize_structure![placementIndex];
        for (let payoutIndex = 0; payoutIndex < (placement.payouts?.length ?? 0); payoutIndex++) {
            const payout = placement.payouts![payoutIndex];
            rows.push({
                placementIndex,
                payoutIndex,
                title: formatFundingAwardTitle(placement.position, payout.label),
                prize: Number(payout.prize) || 0,
            });
        }
    }
    return rows;
}

export function updateQuickCreateAwardPrize(
    categories: ICampaignCategory[],
    placementIndex: number,
    payoutIndex: number,
    prize: number,
): ICampaignCategory[] {
    const cats = cloneQuickCreateCategories(categories.length ? categories : [emptyWizardCategory()]);
    const placement = cats[0]?.prize_structure?.[placementIndex];
    const payout = placement?.payouts?.[payoutIndex];
    if (!placement || !payout) return categories;
    payout.prize = prize;
    return cats;
}

export function removeQuickCreateAwardPayout(
    categories: ICampaignCategory[],
    placementIndex: number,
    payoutIndex: number,
): ICampaignCategory[] {
    const cats = cloneQuickCreateCategories(categories.length ? categories : [emptyWizardCategory()]);
    const structure = [...(cats[0]?.prize_structure || [])];
    const placement = structure[placementIndex];
    if (!placement) return categories;

    const payouts = [...(placement.payouts || [])];
    payouts.splice(payoutIndex, 1);
    if (payouts.length === 0) {
        structure.splice(placementIndex, 1);
    } else {
        structure[placementIndex] = { ...placement, payouts };
    }
    cats[0] = { ...cats[0], prize_structure: structure };
    return cats;
}

export function getQuickCreateTierSummary(category: ICampaignCategory): string {
    const tiers = (category.tier_fee || []).filter(t => t.label?.trim());
    if (!tiers.length) {
        return tierLabelsFromCategory(category).join(', ');
    }
    return tiers
        .map(t => {
            const fee = Number(t.fee);
            return fee > 0 ? `${t.label} ($${fee})` : t.label;
        })
        .join(', ');
}

export function getQuickCreateAwardSummaryLines(
    categories: ICampaignCategory[],
): Array<{ title: string; amount: string }> {
    return buildFundingAwardRowsFromCategories(categories).map(row => ({
        title: row.displayTitle,
        amount: formatUsdFromCents(row.prizeCents),
    }));
}

export function buildContestQuickCreateAutofill(): ContestQuickCreateDraft {
    const submission = SUBMISSION_TYPE_OPTIONS[0];
    const category = sampleQuickCreateCategory();

    return {
        submission_state: submission.value,
        submission_state_message: submission.message,
        title: pick(CONTEST_TITLES),
        description: pick(CONTEST_DESCRIPTIONS),
        requirements: REQUIREMENT_SAMPLES.slice(0, 3),
        categories_enabled: true,
        categories: [category],
    };
}

export function quickCreateDraftFromWizard(state: ContestWizardState): ContestQuickCreateDraft {
    const fallback = buildContestQuickCreateAutofill();
    const plainDescription = (state.description || '').replace(/<[^>]+>/g, '').trim();

    const hasTitle = Boolean(state.title.trim());
    const hasDescription = Boolean(plainDescription);
    const hasRequirements = state.requirements.some(r => r.trim());
    const hasCategory =
        state.categories_enabled &&
        state.categories.some(c => c.category_name?.trim() && plainTextFromHtml(c.category_description || ''));

    if (!hasTitle && !hasDescription && !hasRequirements && !hasCategory) {
        return fallback;
    }

    const categories =
        state.categories.length && state.categories[0]?.category_name?.trim() ? state.categories : fallback.categories;

    return {
        submission_state: state.submission_state,
        submission_state_message: state.submission_state_message,
        title: state.title.trim() || fallback.title,
        description: plainDescription || fallback.description,
        requirements: hasRequirements ? state.requirements : fallback.requirements,
        categories_enabled: state.categories_enabled,
        categories,
    };
}

export function isContestQuickCreateValid(draft: ContestQuickCreateDraft): boolean {
    if (!draft.submission_state) return false;
    if (!draft.title.trim() || !plainTextFromHtml(draft.description)) return false;
    if (!draft.requirements.some(r => r.trim())) return false;
    if (draft.categories_enabled && !isQuickCreateCategoryAwardsConfigured(draft)) {
        return false;
    }
    return true;
}

export function mergeQuickCreateDraftToWizardState(
    draft: ContestQuickCreateDraft,
    existing: ContestWizardState = CONTEST_WIZARD_INITIAL,
): ContestWizardState {
    const timeline =
        existing.contest_phases_enabled && existing.contest_phase_dates.length
            ? {
                  contest_phases_enabled: true as const,
                  phases: existing.contest_phase_dates,
              }
            : generateTimelinePhasesFromAnswers(
                  {
                      start_date: dayjs().add(7, 'day').format('YYYY-MM-DD'),
                      selected_phases: 'submission,judging,vote',
                      phase_duration_days: '14',
                  },
                  draft.submission_state,
              );

    const defaultScoreCriteria = criteriaLabelsFromSelection(
        'short_film',
        parseCsvSelection(defaultCriteriaSelection('short_film')),
    );

    return {
        ...CONTEST_WIZARD_INITIAL,
        ...existing,
        submission_state: draft.submission_state,
        submission_state_message: draft.submission_state_message,
        title: draft.title.trim(),
        description: draft.description.trim(),
        requirements: draft.requirements.filter(r => r.trim()),
        categories_enabled: draft.categories_enabled,
        categories: draft.categories_enabled ? draft.categories : [],
        contest_phases_enabled: timeline.contest_phases_enabled,
        contest_phase_dates: timeline.phases,
        voting_required: draft.submission_state === 'public_submission' ? existing.voting_required : false,
        tag: existing.tag.length ? existing.tag : ['short film'],
        genre: existing.genre.length ? existing.genre : ['film festival'],
        score_criteria: existing.score_criteria.length ? existing.score_criteria : defaultScoreCriteria,
    };
}
